import { isISODateString, localizeDate } from '@/shared/utils/dateUtils'
import { JSONtoCsv, resolveFileOnBrowser } from '@/shared/utils/filesUtils'
import { ExportData } from '@/shared/v2/shared/components/ExportDataDialog'
import { And, GreaterThanOrEqual, LessThanOrEqual } from '@hello-aria/query-string'
import { FilterParamsInput } from '@hello-aria/query-string/dist/stringify'
import { format } from 'date-fns'
import { isNil } from 'lodash'
import { ExportKey } from '../hooks/useExports'

export type FormatConfig<T extends Record<string, any>> = {
    locale?: string
    amountFields?: Array<keyof T>
    dateFields?: Array<keyof T>
    numberFields?: Array<keyof T>
    mapKeys?: Partial<Record<keyof T, string>>
}

type KeysFromArr<A> = A extends ReadonlyArray<infer U> ? U : never
type OutValueForKey<T, K extends keyof T, AmountK, DateK> = K extends AmountK
    ? string
    : K extends DateK
      ? string
      : T[K] extends number
        ? number
        : T[K] extends string
          ? T[K]
          : T[K] extends Date
            ? string
            : T[K] extends Array<any>
              ? string
              : string

type RemapKey<K extends PropertyKey, M> = K extends keyof M ? (M[K] extends PropertyKey ? M[K] : K) : K

export type FormatResult<T extends Record<string, any>, AmountK extends keyof T, DateK extends keyof T> = {
    [K in keyof T]: OutValueForKey<T, K, AmountK, DateK>
}

export type FormatResultWithMap<
    T extends Record<string, any>,
    AmountK extends keyof T,
    DateK extends keyof T,
    M extends Partial<Record<keyof T, string>>
> = {
    [K in keyof T as RemapKey<K, M>]: OutValueForKey<T, K, AmountK, DateK>
}

function formatGeneric(value: unknown, locale: string): string | number {
    if (value == null) return ''

    if (Array.isArray(value)) {
        return value.map((v) => (v == null ? '' : String(v))).join(', ')
    }

    if (value instanceof Date) {
        return localizeDate(value, { locale }) ?? ''
    }

    if (typeof value === 'string') {
        const isoLike = isISODateString(value)
        if (isoLike && !Number.isNaN(Date.parse(value))) {
            return localizeDate(value, { locale }) ?? ''
        }
        return value
    }

    if (typeof value === 'number') return value

    if (typeof value === 'object') {
        try {
            return JSON.stringify(value)
        } catch {
            return String(value)
        }
    }

    return String(value)
}

export function formatRows<
    T extends Record<string, any>,
    const AF extends ReadonlyArray<keyof T> = readonly (keyof T)[],
    const DF extends ReadonlyArray<keyof T> = readonly (keyof T)[],
    const NF extends ReadonlyArray<keyof T> = readonly (keyof T)[]
>(
    rows: T[] | undefined | null,
    config: Omit<FormatConfig<T>, 'amountFields' | 'dateFields' | 'numberFields' | 'mapKeys' | 'currencyKey'> & {
        amountFields?: AF
        dateFields?: DF
        numberFields?: NF
        mapKeys?: undefined
    }
): Array<FormatResult<T, KeysFromArr<AF>, KeysFromArr<DF>>>

export function formatRows<
    T extends Record<string, any>,
    const AF extends ReadonlyArray<keyof T> = readonly (keyof T)[],
    const DF extends ReadonlyArray<keyof T> = readonly (keyof T)[],
    const NF extends ReadonlyArray<keyof T> = readonly (keyof T)[],
    const M extends Partial<Record<keyof T, string>> = Partial<Record<keyof T, string>>
>(
    rows: T[] | undefined | null,
    config: Omit<FormatConfig<T>, 'amountFields' | 'dateFields' | 'numberFields'> & {
        amountFields?: AF
        dateFields?: DF
        numberFields?: NF
        mapKeys: M
    }
): Array<FormatResultWithMap<T, KeysFromArr<AF>, KeysFromArr<DF>, M>>

export function formatRows<
    T extends Record<string, any>,
    AF extends ReadonlyArray<keyof T> = readonly (keyof T)[],
    DF extends ReadonlyArray<keyof T> = readonly (keyof T)[],
    NF extends ReadonlyArray<keyof T> = readonly (keyof T)[],
    M extends Partial<Record<keyof T, string>> | undefined = undefined
>(
    rows: T[] | undefined | null,
    config: Omit<FormatConfig<T>, 'amountFields' | 'dateFields' | 'numberFields'> & {
        amountFields?: AF
        dateFields?: DF
        numberFields?: NF
        mapKeys?: M
    }
): Array<any> {
    if (!rows || rows.length === 0) return []

    const {
        locale = 'en',
        amountFields = [] as unknown as AF,
        dateFields = [] as unknown as DF,
        numberFields = [] as unknown as NF,
        mapKeys
    } = config

    const amountKeys = new Set<keyof T>(amountFields as ReadonlyArray<keyof T>)
    const dateKeySet = new Set<keyof T>(dateFields as ReadonlyArray<keyof T>)
    const numberDefaultsSet = new Set<keyof T>(numberFields as ReadonlyArray<keyof T>)

    return rows.map((row) => {
        const out: Record<string, string | number> = {}

        ;(Object.keys(row) as Array<keyof T>).forEach((key) => {
            const outputKey = (mapKeys?.[key] ?? (key as string)) as string
            const raw = row[key]

            if (amountKeys.has(key)) {
                const amountVal = Number(raw ?? 0) || 0
                out[outputKey] = amountVal
                return
            }

            if (dateKeySet.has(key)) {
                out[outputKey] =
                    raw == null ? '' : (localizeDate(raw, { locale, dateFormatOptions: { hasHours: true } }) ?? '')
                return
            }

            if (numberDefaultsSet.has(key)) {
                if (raw == null) {
                    out[outputKey] = 0
                } else if (typeof raw === 'number') {
                    out[outputKey] = raw
                } else {
                    const n = Number(raw)
                    out[outputKey] = Number.isFinite(n) ? n : formatGeneric(raw, locale)
                }
                return
            }

            out[outputKey] = formatGeneric(raw, locale)
        })

        return out
    })
}

const mapName: Record<ExportKey, string> = {
    loans: 'advances',
    creditLimits: 'credit-limits',
    repayments: 'repayments',
    redirections: 'redirections'
}

export const resolveExportFileName = (key: ExportKey, dates: ExportData | undefined) => {
    const dateFormat = 'dd-MM-yyyy'

    if (!isNil(dates) && !isNil(dates.startDate) && !isNil(dates.endDate)) {
        return `aria-${mapName[key]}-${format(dates.startDate, dateFormat)}-${format(dates.endDate, dateFormat)}.csv`
    }

    return `aria-${mapName[key]}.csv`
}

export const prepareAndDownloadExportFile = (response: Record<string, any>[], key: ExportKey, payload: ExportData) => {
    const csv = new Blob([JSONtoCsv(response)], {
        type: 'text/plain;charset=utf-8'
    })
    const fileName = resolveExportFileName(key, payload)
    resolveFileOnBrowser(csv, fileName, true)
    return
}

export const buildExportQuery = (payload: ExportData): FilterParamsInput[] => {
    if (isNil(payload) || (isNil(payload.startDate) && isNil(payload.endDate))) {
        return []
    }

    return [
        And(
            ...(payload.startDate ? [GreaterThanOrEqual('createdAt', payload.startDate.toISOString())] : []),
            ...(payload.endDate ? [LessThanOrEqual('createdAt', payload.endDate.toISOString())] : [])
        )
    ]
}
